import {useMemo} from 'react';

import {
  type AllRunGroupsQuery,
  type AllRunGroupsQueryVariables,
  type Exact,
  type Scalars,
} from '../../../generated/graphql';
import {
  type ApolloQueryResult,
  useQuery,
} from '../../../state/graphql/apolloWithAbort';
import {ALL_RUN_GROUPS} from './AllRunGroups.query';

// Type guard for doing typesafe filtering later on.
function isA<T>(v: unknown): v is T {
  return typeof v === 'string';
}

type UseRunGroupsHook = {
  loading: boolean;
  groups: string[];
  refetch: (
    variables?:
      | Exact<{
          projectName: Scalars['String']['input'];
          entityName: Scalars['String']['input'];
        }>
      | undefined
  ) => Promise<ApolloQueryResult<AllRunGroupsQuery>>;
};
/**
 * Returns all the run groups in this project. This operation can be expensive
 * for large projects, like openai/gpt-4. A wrapper over AllRunGroups.query,
 * but returns an array of strings rather than the query response, which seems
 * more ergonomic.
 *
 * @param entityName
 * @param projectName
 */
export function useRunGroups(
  entityName: string,
  projectName: string
): UseRunGroupsHook {
  const {loading, data, refetch} = useQuery<
    AllRunGroupsQuery,
    AllRunGroupsQueryVariables
  >(ALL_RUN_GROUPS, {
    variables: {
      entityName,
      projectName,
    },
  });

  const runs = data?.project?.runs?.edges;
  const groups = useMemo(
    () => (runs ? runs.map(e => e.node.group).filter(isA<string>) : []),
    [runs]
  );

  return {loading, groups, refetch};
}
